function toHex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer))
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Hashes a corporate email into a stable hex digest suitable for use as a
 * Mixpanel distinct_id, keyed with `secret` (HMAC-SHA256) rather than a
 * plain digest. Corporate emails follow a low-entropy, enumerable pattern
 * (firstname.lastname@arrive.com), so an unkeyed SHA-256 is a dictionary
 * attack away from being reversed by anyone with Mixpanel access — HMAC
 * makes that infeasible without also knowing `secret`.
 *
 * `secret` still reaches the browser via app-config, so this is a
 * mitigation, not a guarantee of non-reversibility — a sufficiently
 * motivated holder of both the Mixpanel export and the deployed frontend
 * config can still reconstruct it. True opacity requires computing the
 * hash server-side, where the secret never leaves the backend; that is a
 * separate follow-up (see PAT-915 discussion), not done here.
 *
 * Trims and lowercases first so the same person always produces the same
 * hash, even if the identity provider returns the email with different
 * casing/whitespace across sessions.
 */
export async function hashEmail(
  email: string,
  secret: string,
): Promise<string> {
  const normalized = email.trim().toLowerCase();
  if (!normalized) {
    throw new Error('hashEmail: email must not be empty');
  }
  if (!secret) {
    throw new Error('hashEmail: secret must not be empty');
  }
  if (!globalThis.crypto?.subtle) {
    throw new Error('hashEmail: Web Crypto API is unavailable');
  }

  const key = await globalThis.crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const signature = await globalThis.crypto.subtle.sign(
    'HMAC',
    key,
    new TextEncoder().encode(normalized),
  );
  return toHex(signature);
}
